import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEventCallback } from 'usehooks-ts';

import {
  DuoActivity,
  DuoActivitySkeleton,
  DuoNotFound,
} from '@/features/duos/components/duoActivity';
import { getDuoDetailsOptions } from '@/features/duos/hooks/useGetDuoDetails/getDuoDetails.options';
import { getValorantPlayerOptions } from '@/features/games/valorant/hooks/useGetValorantPlayer';
import { getProfileOptions } from '@/features/profile/hooks/useGetProfile';
import { MainLayout } from '@/shared/components/mainLayout';
import { PageHeader } from '@/shared/components/pageHeader';

export const Route = createFileRoute(
  '/_protected/_app/_mainLayout/my-duos/$id',
)({
  loader: async ({ context, params }) => {
    const duoData = await context.queryClient.ensureQueryData(
      getDuoDetailsOptions({ userId: params.id }),
    );

    const [firstPartner, secondPartner] = duoData.duo.partners;
    const firstPartnerUserId = firstPartner.userId;
    const secondPartnerUserId = secondPartner.userId;

    const [firstPartnerProfile, secondPartnerProfile] = await Promise.all([
      context.queryClient.ensureQueryData(
        getProfileOptions({ userId: firstPartnerUserId }),
      ),
      context.queryClient.ensureQueryData(
        getProfileOptions({ userId: secondPartnerUserId }),
      ),
    ]);

    if (firstPartnerProfile.profile?.riotPuuid) {
      void context.queryClient.prefetchQuery(
        getValorantPlayerOptions({
          puuid: firstPartnerProfile.profile.riotPuuid,
        }),
      );
    }
    if (secondPartnerProfile.profile?.riotPuuid) {
      void context.queryClient.prefetchQuery(
        getValorantPlayerOptions({
          puuid: secondPartnerProfile.profile.riotPuuid,
        }),
      );
    }
  },
  errorComponent: () => (
    <MainLayout.Root className="h-full">
      <PageHeader.Root>
        <PageHeader.BackButton
          onClick={() => {
            window.history.back();
          }}
        />
      </PageHeader.Root>
      <MainLayout.Content className="mb-4 flex min-h-0 w-full flex-col items-center px-4 pt-18.5">
        <DuoNotFound className="h-full max-h-full w-full max-w-100 flex-1 overflow-hidden md:max-h-165" />
      </MainLayout.Content>
    </MainLayout.Root>
  ),
  pendingComponent: () => (
    <MainLayout.Root className="h-full">
      <PageHeader.Root>
        <PageHeader.BackButton
          onClick={() => {
            window.history.back();
          }}
        />
      </PageHeader.Root>
      <MainLayout.Content className="mb-4 flex min-h-0 w-full flex-col items-center px-4 pt-18.5">
        <DuoActivitySkeleton className="h-full max-h-full w-full max-w-100 flex-1 overflow-hidden md:max-h-165" />
      </MainLayout.Content>
    </MainLayout.Root>
  ),
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const handleGoBack = useEventCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      void navigate({ to: '/my-duos' });
    }
  });

  return (
    <MainLayout.Root className="h-full">
      <PageHeader.Root>
        <PageHeader.BackButton onClick={handleGoBack} />
      </PageHeader.Root>
      <MainLayout.Content className="mb-4 flex min-h-0 w-full flex-col items-center px-4 pt-18.5">
        <DuoActivity className="max-h-full w-full max-w-100" userId={id} />
      </MainLayout.Content>
    </MainLayout.Root>
  );
}
